import React, { useState, useEffect } from 'react';
import { useNavigation } from '../../context/NavigationContext';
import {
	BringzzPageContainer,
	BringzzIcon,
	BringzzText,
	BringzzBottomDrawer,
	BringzzRadioList,
	BringzzRadio,
	BringzzCheckbox
} from '@bringzz/components';
import useRequest from 'hooks/useRequest';
import { useAuth } from 'context/AuthContext';
import { useTranslation } from 'react-i18next';

function formatDate(dateString) {
	const date = new Date(dateString);
	const day = String(date.getDate()).padStart(2, '0');
	const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
	const year = date.getFullYear();

	return `${day}.${month}.${year}`;
}

// Helper function to format field values
const formatFieldValue = (values) => {
	if (!values || values.length === 0) return null;
	
	return values
		.filter(val => val !== null && val !== undefined && val !== '')
		.join(' | ');
};

const AccountNamePage = () => {
	const { backPress, navigate } = useNavigation();
	const { user, refreshUserData } = useAuth();
	const { t } = useTranslation();

	const { sendRequest, data, error, loading } = useRequest();

	const consumerFields = [
		{ 
			name: t('component.account.name.title'), 
			value: formatFieldValue([user?.firstName, user?.lastName]), 
			path: 'account/name' 
		},
		{ 
			name: t('component.account.email.title'), 
			value: user?.emailAddress, 
			path: 'account/email' 
		},
		{
			name: t('component.account.mobile_number'),
			value: user?.phoneNumber,
			path: 'account/mobile'
		},
		{ 
			name: t('component.account.svnr.title'), 
			value: user?.stateInsuranceId || "", 
			path: 'account/svnr' 
		},
		{
			name: t('component.account.private_insurance.title'),
			value: formatFieldValue([user?.privateInsuranceCompanyname, user?.privateInsuranceId]),
			path: 'account/insurance'
		},
		{
			name: t('component.account.doctor_verification.title'),
			value: formatFieldValue([user?.doctorType, `ID: ${user?.doctorId}`, `UID: ${user?.doctorUid}`]),
			path: 'account/doctor'
		}
	].filter(field => field.value !== null);

	const pharmacistFields = [
		{
			name: t('component.account.name.title'),
			value: formatFieldValue([user?.firstName, user?.lastName, user?.birthday]),
			path: 'account/name'
		},
		{ 
			name: t('component.account.email.title'), 
			value: user?.emailAddress, 
			path: 'account/email' 
		},
		{
			name: t('component.account.mobile_number'),
			value: user?.phoneNumber,
			path: 'account/mobile'
		},
		{ 
			name: t('component.account.bio.title'), 
			value: user?.description, 
			path: 'account/bio' 
		}
	].filter(field => field.value !== null);

	const shopperFields = [
		{
			name: t('component.account.name.title'),
			value: formatFieldValue([user?.firstName, user?.lastName, user?.birthday]),
			path: 'account/name'
		},
		{ 
			name: t('component.account.email.title'), 
			value: user?.emailAddress, 
			path: 'account/email' 
		},
		{
			name: t('component.account.mobile_number'),
			value: user?.phoneNumber,
			path: 'account/mobile'
		},
		{
			name: t('component.account.address.title'),
			value: 'Musterstastrsese 10, 1040 Wien',
			path: 'address'
		},
		{ 
			name: t('component.account.id_verification'), 
			value: formatFieldValue(['Passport', 'P123456789']), 
			path: 'id' 
		}
	].filter(field => field.value !== null);

	let state = {
		checked: <div></div>,
		unChecked: <div></div>
	};

	const [fields, setFields] = useState([]);
	const [pickerDrawerOpened, setPickerDrawerOpened] = useState(null);
	const [profileImage, setProfileImage] = useState(user.userPic || null);
	const [hide, setHide] = useState(user.hideProfile || false);

	useEffect(() => {
		if (user) {
			switch (user.userRole) {
				case 1:
					setFields(pharmacistFields);
					break;
				case 4:
					setFields(shopperFields);
					break;
				case 5:
					setFields(consumerFields);
					break;
				default:
					setFields([]);
					break;
			}
		}
	}, [user]);

	const postImage = async (file) => {
		const formData = new FormData();
		formData.append('profilePicture', file);

		const result = await sendRequest({
			method: 'POST',
			url: `/module/profile/changeProfilePicture`,
			data: formData,
			headers: {
				'Content-Type': undefined
			}
		})
			.then(response => {
				if (response.status === 'success') {
					return response
				};
			})
			.catch(error => {
				console.error(error);
				return false;
			});

		if (result) refreshUserData()
	};

	const handlePickerClick = async choice => {
		try {
			if (choice === 'camera') {
				// Handling camera input
				const stream = await navigator.mediaDevices.getUserMedia({
					video: true
				});
				const video = document.createElement('video');
				video.srcObject = stream;
				video.play();

				const canvas = document.createElement('canvas');
				const context = canvas.getContext('2d');
				video.onloadedmetadata = () => {
					canvas.width = video.videoWidth;
					canvas.height = video.videoHeight;
					context.drawImage(video, 0, 0, canvas.width, canvas.height);
					const base64Image = canvas.toDataURL('image/jpeg');
					// Stop the video stream
					stream.getTracks().forEach(track => track.stop());
					setProfileImage(base64Image);
					// Convert canvas to blob and create file
					canvas.toBlob(blob => {
						const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
						postImage(file);
					}, 'image/jpeg');
				};
			} else if (choice === 'photos' || choice === 'file') {
				// Handling gallery/file input
				const input = document.createElement('input');
				input.type = 'file';
				input.accept = 'image/*';
				input.onchange = async event => {
					const file = event.target.files[0];
					if (file) {
						const reader = new FileReader();
						reader.onloadend = () => {
							setProfileImage(reader.result);
						};
						reader.readAsDataURL(file);
						postImage(file);
					}
				};
				input.click();
			}
		} catch (error) {
			console.error('Error handling picker choice:', error);
		}
	};

	return (
		<BringzzPageContainer
			header={{
				left: (
					<BringzzIcon
						size='22'
						icon='IconChevronLeft'
						folder='LineIcons'
						className='cursor-pointer'
						onClick={backPress}
					/>
				),
				title: <BringzzText tag='h2'>{t('component.account.my_account')}</BringzzText>
			}}
		>
			<div className='absolute inset-0 top-14 bottom-24 overflow-y-auto'>
				<div className='p-4 mt-8 divide-y divide-solid'>
				{user && user.userRole !== 4 && (
					<div className='mt-4 mb-8 flex justify-center'>
						<div className='space-y-4 relative'>
							<div
								className='flex justify-center text-white font-bold text-4xl items-center rounded-full bg-midnight-blue h-44 w-44'
								style={{
									backgroundImage: profileImage ? `url(${profileImage})` : '',
									backgroundSize: 'cover',
									backgroundPosition: 'center'
								}}
							>
								{!profileImage && user && user.firstName.charAt(0)}
							</div>
							<div
								className='bg-magic-lilac h-12 w-12 absolute bottom-0 rounded-full right-2'
								onClick={() => setPickerDrawerOpened(true)}
							>
								<BringzzIcon
									size='24'
									icon='IconPlus'
									folder='LineIcons'
									className='cursor-pointer flex h-full flex-col items-center justify-center'
								/>
							</div>
						</div>
					</div>
				)}
				{fields.map((field, index) => (
					<div
						key={index}
						className='flex items-center justify-between py-4 px-2 cursor-pointer'
						onClick={() => field.path && navigate(`settings/${field.path}`)}
					>
						<div className='flex flex-col'>
							<BringzzText tag='h3'>{field.name}</BringzzText>
							{field.value && (
								<div className='w-64'>
									<BringzzText
										tag='h3'
										className='text-normal text-black/50 whitespace-pre-line truncate'
									>
										{field.value}
									</BringzzText>
								</div>
							)}
						</div>
						<BringzzIcon size='18' icon={'IconPencil'} folder={'LineIcons'} />
					</div>
				))}
				{user && user.userRole === 1 && (
					<div className='px-2 py-4'>
						<BringzzCheckbox
							id='example-checkbox2'
							label={t('component.account.hide_profile_team')}
							checked={hide}
							onChange={hideChecked =>
								setHide(() => {
									sendRequest({
										method: 'POST',
										url: `/module/profile/changeHideProfile`,
										data: { hideProfile: hideChecked }
									})
										.then(response => {
											if (response.status === 'success') {
												refreshUserData()
												return hideChecked
											};
										})
										.catch(error => {
											console.log(error);
											return hideChecked;
										});
								})
							}
						/>
					</div>
				)}
				
				</div>
			</div>

			<BringzzBottomDrawer
				isOpen={pickerDrawerOpened}
				title={t('component.account.update_profile_picture')}
				close={() => setPickerDrawerOpened(false)}
			>
				<div className='px-4 py-6'>
					<BringzzRadioList
						state={state}
						className='rounded-xl w-full text-center'
						onChange={choice => {
							handlePickerClick(choice);
							setPickerDrawerOpened(false);
						}}
					>
						<BringzzRadio
							value='camera'
							className='p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400 flex'
						>
							<div>{t('component.account.camera')}</div>
						</BringzzRadio>
						<BringzzRadio
							value='photos'
							className='p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400'
						>
							<div>{t('component.account.photos')}</div>
						</BringzzRadio>
						<BringzzRadio
							className='p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400'
							value='file'
						>
							<div>{t('component.account.file')}</div>
						</BringzzRadio>
					</BringzzRadioList>
				</div>
			</BringzzBottomDrawer>
		</BringzzPageContainer>
	);
};

export default AccountNamePage;
